import {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {EllipsisVertical, Lock, Pause, Plus, RotateCw, Search, TriangleAlert, Webhook as WebhookIcon} from 'lucide-react';
import {PhoneHQLoader, PhoneHQButton, PhoneHQDialog, PhoneHQDialogFooter, PhoneHQDropdown} from 'ch3-ui-lib';
import {useAppDispatch} from '../../../store/store';
import {showToast} from '../../../store/features/Notifications/notificationsSlice';
import {
    useCreateWebhookConfigurationMutation,
    useDeleteWebhookMutation,
    useGetWebhookConfigurationQuery,
    useGetWebhooksQuery,
    useRetryFailedWebhookDeliveriesMutation,
    useSendWebhookTestMutation,
    useUpdateWebhookConfigurationMutation,
    useUpdateWebhookMutation,
    type Webhook,
} from '../../../store/features/integrations/integrationsService';
import {
    dateTime,
    deliveryError,
    endpointLabel,
    errorStatus,
    eventTypeLabel,
    healthNote,
    matchesWebhookFilter,
    matchesWebhookQuery,
    retryFailedSince,
    scopeSummary,
    shortDate,
    WEBHOOK_FILTERS,
    type WebhookFilter,
    webhookKind,
} from './webhookModel';
import {BreadcrumbBar, FilterPills, HealthBadge, HealthTicks, Toggle, ToggleTrack, WebhookTile} from './webhookUi';
import {SigningSecretCard} from './SigningSecretCard';
import {WebhookFormDrawer} from './WebhookFormDrawer';

const COLUMNS = 'grid-cols-[minmax(180px,2fr)_minmax(110px,1.1fr)_minmax(110px,1fr)_minmax(160px,1.4fr)_56px_32px]';
const LIST_PATH = '/admin/webhook/list';
const POLL_MS = 30_000;

export default function WebhookList() {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const {id: routeId} = useParams<{id: string}>();
    const formTarget = routeId === 'new' ? 'new' : routeId && routeId !== 'list' ? Number(routeId) : undefined;
    const [query, setQuery] = useState('');
    const [filter, setFilter] = useState<WebhookFilter>('all');
    const [deleting, setDeleting] = useState<Webhook>();

    const {data, isLoading, isError} = useGetWebhooksQuery({include: 'PHONE_NUMBERS,RECENT_DELIVERIES'}, {refetchOnMountOrArgChange: true, pollingInterval: POLL_MS});
    const {data: configurations, isSuccess: configurationLoaded} = useGetWebhookConfigurationQuery();
    const [createConfiguration] = useCreateWebhookConfigurationMutation();
    const [updateConfiguration, {isLoading: switching}] = useUpdateWebhookConfigurationMutation();
    const [updateWebhook] = useUpdateWebhookMutation();
    const [deleteWebhook, {isLoading: removing}] = useDeleteWebhookMutation();
    const [sendWebhookTest] = useSendWebhookTestMutation();
    const [retryFailedDeliveries] = useRetryFailedWebhookDeliveriesMutation();

    const configuration = configurations?.data?.[0];
    useEffect(() => {
        if (configurationLoaded && !configuration) void createConfiguration({body: {status: 'DISABLED'}});
    }, [configurationLoaded, configuration, createConfiguration]);

    const notify = (description: string, type: 'success' | 'error' = 'success') => dispatch(showToast({description, type}));
    const sending = configuration?.status === 'ENABLED';
    const webhooks = data?.data ?? [];
    const failing = webhooks.filter(webhook => webhookKind(webhook) === 'failing');
    const filters = WEBHOOK_FILTERS.map(([id, label]) => ({id, label, count: webhooks.filter(webhook => matchesWebhookFilter(webhookKind(webhook), id)).length}));
    const rows = webhooks.filter(webhook => matchesWebhookFilter(webhookKind(webhook), filter) && matchesWebhookQuery(webhook, query));

    const openLog = (webhook: Webhook) => navigate(`/admin/webhook/${webhook.id}/deliveries`);
    const openForm = (target: number | 'new') => navigate(`/admin/webhook/${target}`);

    const toggleSending = async () => {
        try {
            await updateConfiguration({status: sending ? 'DISABLED' : 'ENABLED'}).unwrap();
            notify(sending ? 'Sending paused for all webhooks' : 'Sending resumed');
        } catch {
            notify('Sending could not be changed', 'error');
        }
    };

    const toggleWebhook = async (webhook: Webhook, on: boolean) => {
        try {
            await updateWebhook({id: webhook.id, data: {status: on ? 'ENABLED' : 'DISABLED'}}).unwrap();
            notify(`“${webhook.name}” turned ${on ? 'on' : 'off'}`);
        } catch {
            notify('Webhook could not be updated', 'error');
        }
    };

    const sendTest = async (webhook: Webhook) => {
        try {
            await sendWebhookTest({id: webhook.id}).unwrap();
            notify(`Test event queued for “${webhook.name}”`);
        } catch (error) {
            notify(errorStatus(error) === 409 ? 'Turn the webhook on to send a test event.' : 'Test event could not be sent.', 'error');
        }
    };

    const retryFailed = async (webhook: Webhook) => {
        try {
            const {queued} = await retryFailedDeliveries({id: webhook.id, since: retryFailedSince(webhook)}).unwrap();
            notify(queued > 0 ? `${queued} ${queued === 1 ? 'delivery' : 'deliveries'} queued for retry` : 'No failed deliveries to retry');
        } catch {
            notify('Failed deliveries could not be retried', 'error');
        }
    };

    const remove = async () => {
        if (!deleting) return;
        try {
            await deleteWebhook({id: deleting.id}).unwrap();
            notify(`“${deleting.name}” deleted`);
            setDeleting(undefined);
        } catch {
            notify('Webhook could not be deleted', 'error');
        }
    };

    return <div className="flex min-h-full flex-col">
        <BreadcrumbBar items={[{label: 'Integrations', to: '/admin/integrations'}, {label: 'Webhooks'}]}/>
        <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-5 px-5 pb-16 pt-7 sm:px-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3.5">
                    <WebhookTile/>
                    <div>
                        <h1 className="text-[22px] font-semibold leading-tight tracking-[-0.01em] text-ink">Webhooks</h1>
                        <p className="mt-0.5 text-sm text-ink-muted">Send call events to your own systems in real time.</p>
                    </div>
                </div>
                <div className="flex items-center gap-2.5">
                    <button type="button" role="switch" aria-checked={sending} disabled={!configuration || switching} onClick={() => void toggleSending()}
                        className="inline-flex h-[34px] items-center gap-2.5 rounded-md border border-line bg-surface pl-3 pr-2.5 text-[13px] font-medium text-ink disabled:opacity-50">
                        Sending<ToggleTrack checked={sending} small/>
                    </button>
                    <PhoneHQButton phq-id="webhookList-addNewBtn" variant="primary" size="sm" onClick={() => openForm('new')}>
                        <Plus size={14} className="!p-0 !text-white"/>New webhook
                    </PhoneHQButton>
                </div>
            </div>

            {configuration && !sending && <div className="flex flex-wrap items-center gap-3 rounded-[10px] border border-line bg-zinc-50 px-4 py-3">
                <Pause size={18} className="!p-0 shrink-0 text-ink-muted"/>
                <p className="min-w-[240px] flex-1 text-[13.5px] text-ink-secondary"><strong className="font-semibold text-ink">Sending is paused for all webhooks.</strong> No events are delivered until you turn it back on.</p>
                <PhoneHQButton variant="secondary" size="sm" disabled={switching} onClick={() => void toggleSending()}>Resume sending</PhoneHQButton>
            </div>}

            {failing.map(webhook => <FailingBanner key={webhook.id} webhook={webhook} onView={() => openLog(webhook)} onRetry={() => void retryFailed(webhook)}/>)}

            <SigningSecretCard secret={configuration?.secretKey}/>

            {isLoading && <div className="flex justify-center py-10"><PhoneHQLoader/></div>}
            {isError && <p role="alert" className="py-10 text-center text-sm text-danger-ink">Webhooks could not be loaded. Refresh the page to try again.</p>}

            {data && webhooks.length === 0 && <EmptyState onCreate={() => openForm('new')}/>}

            {webhooks.length > 0 && <>
                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative h-[34px] min-w-[200px] flex-[0_1_300px]">
                        <Search size={15} className="!p-0 pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-disabled"/>
                        <input type="search" aria-label="Search webhooks" value={query} placeholder="Search by name or URL…" onChange={event => setQuery(event.target.value)}
                            className="h-full w-full rounded-md border border-line pl-8 pr-2.5 text-[13px] text-ink outline-none placeholder:text-ink-disabled focus:border-indigo-500 focus:ring-[3px] focus:ring-indigo-500/15"/>
                    </div>
                    <FilterPills label="Filter webhooks by health" items={filters} value={filter} onChange={setFilter}/>
                </div>

                <div className="overflow-hidden rounded-[10px] border border-line bg-surface"><div className="overflow-x-auto [contain:inline-size]"><div className="min-w-[760px]">
                    <div className={`grid ${COLUMNS} items-center gap-4 border-b border-line bg-zinc-50 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.05em] text-ink-muted`}>
                        <div>Webhook</div><div>Event</div><div>Phone numbers</div><div>Health · last 24 deliveries</div><div>Active</div><div/>
                    </div>
                    {rows.map(webhook => <WebhookRow key={webhook.id} webhook={webhook}
                        onOpen={() => openLog(webhook)} onEdit={() => openForm(webhook.id)} onTest={() => void sendTest(webhook)}
                        onDelete={() => setDeleting(webhook)} onToggle={on => void toggleWebhook(webhook, on)}/>)}
                    {rows.length === 0 && <div className="border-t border-line px-5 py-9 text-center text-[13.5px] text-ink-muted">No webhooks match your search.</div>}
                </div></div></div>
            </>}

            <div className="flex items-center gap-1.5 text-[12.5px] text-ink-disabled">
                <Lock size={13} className="!p-0"/>Only workspace admins can see and manage webhooks.
            </div>
        </div>

        {formTarget !== undefined && <WebhookFormDrawer webhookId={formTarget} onClose={() => navigate(LIST_PATH)}/>}

        <PhoneHQDialog open={deleting !== undefined} theme="light" title="Delete webhook?" onOpenChange={open => { if (!open) setDeleting(undefined); }}>
            <div className="flex flex-col gap-4 text-sm">
                <p className="leading-5 text-ink-secondary">“{deleting?.name}” and its delivery log are deleted permanently. Events stop being sent to this endpoint right away.</p>
                <PhoneHQDialogFooter>
                    <PhoneHQButton variant="secondary" onClick={() => setDeleting(undefined)}>Cancel</PhoneHQButton>
                    <PhoneHQButton phq-id="webhookList-deleteConfirmBtn" variant="primary" disabled={removing} style={{background: '#dc2626', borderColor: '#dc2626'}} onClick={() => void remove()}>
                        {removing ? 'Deleting…' : 'Delete webhook'}
                    </PhoneHQButton>
                </PhoneHQDialogFooter>
            </div>
        </PhoneHQDialog>
    </div>;
}

function FailingBanner({webhook, onView, onRetry}: {readonly webhook: Webhook; readonly onView: () => void; readonly onRetry: () => void}) {
    const {health} = webhook;
    return <div role="alert" className="flex flex-wrap items-start gap-3 rounded-[10px] border border-danger-tint bg-danger-tint px-4 py-3.5">
        <TriangleAlert size={18} className="!p-0 mt-px shrink-0 !text-danger-ink"/>
        <div className="flex min-w-[260px] flex-1 flex-col gap-1">
            <div className="font-semibold text-danger-ink">{webhook.name} is failing</div>
            <p className="text-[13.5px] leading-normal text-danger-ink">
                Deliveries have returned {deliveryError(health.lastError) || 'errors'} since {dateTime(health.failingSince)}.
                {health.autoDisableAt && ` If they keep failing it will be turned off automatically on ${shortDate(health.autoDisableAt)}.`}
                {health.notifiedAt && ' Admins have been notified by email.'}
            </p>
        </div>
        <div className="flex gap-2">
            <PhoneHQButton variant="secondary" size="sm" className="!text-danger-ink" onClick={onView}>View deliveries</PhoneHQButton>
            <PhoneHQButton variant="primary" size="sm" style={{background: '#dc2626', borderColor: '#dc2626'}} onClick={onRetry}>
                <RotateCw size={13} className="!p-0 !text-white"/>Retry failed
            </PhoneHQButton>
        </div>
    </div>;
}

interface WebhookRowProps {
    readonly webhook: Webhook;
    readonly onOpen: () => void;
    readonly onEdit: () => void;
    readonly onTest: () => void;
    readonly onDelete: () => void;
    readonly onToggle: (on: boolean) => void;
}

function WebhookRow({webhook, onOpen, onEdit, onTest, onDelete, onToggle}: WebhookRowProps) {
    const kind = webhookKind(webhook);
    const off = kind === 'off' || kind === 'autoDisabled';
    const scope = scopeSummary(webhook);
    const recent = webhook.recentDeliveries ?? [];
    return <div onClick={onOpen} className={`grid ${COLUMNS} cursor-pointer items-center gap-4 border-t border-line px-5 py-3.5 hover:bg-zinc-50`}>
        <div className="min-w-0">
            <button type="button" onClick={event => { event.stopPropagation(); onOpen(); }}
                className={`block max-w-full truncate text-left font-semibold hover:underline ${off ? 'text-ink-muted' : 'text-ink'}`}>{webhook.name}</button>
            <div className="mt-0.5 truncate font-mono text-xs text-ink-muted" title={webhook.url}>{endpointLabel(webhook.url)}</div>
        </div>
        <div className="min-w-0 truncate text-[13.5px] text-ink">{webhook.eventTypes.map(eventType => eventTypeLabel(eventType.type)).join(', ') || '—'}</div>
        <div className="min-w-0">
            <div className="text-[13.5px] text-ink">{scope.label}</div>
            {scope.detail && <div className="mt-0.5 truncate text-xs text-ink-muted" title={scope.detail}>{scope.detail}</div>}
        </div>
        <div className="flex min-w-0 flex-col items-start gap-1.5">
            <div className="flex flex-wrap items-center gap-2.5">
                <HealthBadge kind={kind}/>
                {recent.length > 0 && kind !== 'off' && <HealthTicks statuses={recent}/>}
            </div>
            <div className="text-xs text-ink-muted">{healthNote(webhook)}</div>
        </div>
        <div><Toggle label={`${webhook.name} active`} checked={!off} onChange={onToggle}/></div>
        <div className="flex justify-end" onClick={event => event.stopPropagation()}>
            <PhoneHQDropdown modal={false} align="end" trigger={
                <button type="button" aria-label={`More actions for ${webhook.name}`} className="flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-zinc-100 hover:text-ink">
                    <EllipsisVertical size={16} className="!p-0"/>
                </button>
            }>
                <PhoneHQDropdown.Item onClick={onOpen}><span className="w-40 px-1 text-tiny">View deliveries</span></PhoneHQDropdown.Item>
                <PhoneHQDropdown.Item onClick={onEdit}><span className="w-40 px-1 text-tiny">Edit</span></PhoneHQDropdown.Item>
                <PhoneHQDropdown.Item onClick={onTest}><span className="w-40 px-1 text-tiny">Send test event</span></PhoneHQDropdown.Item>
                <PhoneHQDropdown.Separator/>
                <PhoneHQDropdown.Item onClick={onDelete}><span className="w-40 px-1 text-tiny !text-menu-danger">Delete</span></PhoneHQDropdown.Item>
            </PhoneHQDropdown>
        </div>
    </div>;
}

function EmptyState({onCreate}: {readonly onCreate: () => void}) {
    return <div className="flex flex-col items-center gap-2.5 rounded-2xl bg-zinc-50 px-6 py-16 text-center">
        <span className="mb-2.5 flex h-14 w-14 items-center justify-center rounded-[14px] border border-line bg-surface text-ink-secondary shadow-[0_2px_6px_rgba(0,0,0,0.05)]">
            <WebhookIcon size={26} className="!p-0"/>
        </span>
        <h2 className="text-2xl font-semibold tracking-[-0.01em] text-ink">No webhooks yet</h2>
        <p className="max-w-[440px] text-ink-muted">Create a webhook to send call events to your CRM, helpdesk or any HTTPS endpoint.</p>
        <PhoneHQButton variant="primary" size="sm" className="mt-3" onClick={onCreate}>Create webhook</PhoneHQButton>
    </div>;
}
